import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiCheck, FiHeart, FiMinus, FiPlus } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import productData, {
  formatPrice,
  siteConfig,
} from "../Datas/productData";
import "../css/productdetails.scss";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const {
    addToCart,
    wishlist,
    toggleWishlist,
  } = useContext(CartContext);
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0]?.name || ""
  );
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    document.title = product
      ? `${product.name} | ${siteConfig.displayName} ${siteConfig.brandLine}`
      : `Product not found | ${siteConfig.displayName}`;
    setSelectedColor(product?.colors?.[0]?.name || "");
    setQuantity(1);
    setAdded(false);
    setZoomed(false);
  }, [product]);

  const recommendations = useMemo(() => {
    if (!product) return [];
    return productData
      .filter(
        (item) =>
          item.id !== product.id &&
          (item.category === product.category || item.type === product.type)
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-not-found">
        <p>404</p>
        <h1>Product not found</h1>
        <span>The piece may have moved or is no longer available.</span>
        <Link to="/shop">Back to bags</Link>
      </main>
    );
  }

  const saved = wishlist.includes(product.id);
  const discount = product.compareAtPrice
    ? Math.round((1 - product.price / product.compareAtPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    addToCart(product.id, quantity, product.size[0], selectedColor);
    setAdded(true);
  };

  return (
    <main className="product-detail">
      <nav className="product-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/shop">Bags</Link>
        <span>/</span>
        <span>{product.name}</span>
      </nav>

      <div className="product-detail__layout">
        <section className="product-gallery" aria-label="Product images">
          {product.images.map((image, index) => (
            <button
              type="button"
              key={image}
              className={zoomed ? "is-zoomed" : ""}
              onClick={() => setZoomed((value) => !value)}
              aria-label={zoomed ? "Zoom out product image" : "Zoom product image"}
            >
              <img
                src={image}
                alt={`${product.name} view ${index + 1}`}
                loading={index === 0 ? "eager" : "lazy"}
              />
              <span>{zoomed ? <FiMinus /> : <FiPlus />}</span>
            </button>
          ))}
        </section>

        <aside className="product-buy">
          <div className="product-buy__sticky">
            <div className="product-buy__status">
              {product.status === "new" && <span>New</span>}
              {discount > 0 && <span>-{discount}%</span>}
            </div>
            <p className="product-buy__type">{product.type}</p>
            <h1>{product.name}</h1>
            <p className="product-buy__lead">{product.description}</p>
            <div className="product-buy__price">
              <span>{formatPrice(product.price)}</span>
              {product.compareAtPrice && (
                <del>{formatPrice(product.compareAtPrice)}</del>
              )}
              <small>including VAT</small>
            </div>

            <div className="product-buy__option">
              <div className="product-buy__option-title">
                <span>Color</span>
                <strong>{selectedColor}</strong>
              </div>
              <div className="product-buy__swatches">
                {product.colors.map((item) => (
                  <button
                    type="button"
                    key={item.name}
                    className={selectedColor === item.name ? "is-selected" : ""}
                    style={{ "--swatch": item.hex }}
                    onClick={() => {
                      setSelectedColor(item.name);
                      setAdded(false);
                    }}
                    aria-label={`Select ${item.name}`}
                    aria-pressed={selectedColor === item.name}
                  >
                    <span />
                  </button>
                ))}
              </div>
            </div>

            <div className="product-buy__quantity">
              <span>Quantity</span>
              <div>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                  aria-label="Decrease quantity"
                >
                  <FiMinus aria-hidden="true" />
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((value) => Math.min(10, value + 1))}
                  aria-label="Increase quantity"
                >
                  <FiPlus aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="product-buy__actions">
              <button type="button" className="add-button" onClick={handleAddToCart}>
                {added ? (
                  <>
                    <FiCheck aria-hidden="true" /> Added to basket
                  </>
                ) : (
                  "Add to basket"
                )}
              </button>
              <button
                type="button"
                className={`wish-button ${saved ? "is-saved" : ""}`}
                onClick={() => toggleWishlist(product.id)}
                aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
                aria-pressed={saved}
              >
                <FiHeart aria-hidden="true" />
              </button>
            </div>

            {added && (
              <Link
                className="product-buy__basket-link"
                to="/cartpage"
                onClick={() => window.scrollTo(0, 0)}
              >
                View basket
              </Link>
            )}

            <div className="product-buy__delivery">
              <span>Free standard delivery from {formatPrice(siteConfig.shipping.freeThreshold)}</span>
              <span>{siteConfig.shipping.returnDays}-day returns</span>
            </div>

            <div className="product-accordions">
              <details open>
                <summary>Details</summary>
                <p>{product.description}</p>
                <ul>
                  {product.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <small>
                  Article number: LK-{String(product.id).padStart(4, "0")}-26
                </small>
              </details>
              <details>
                <summary>Size &amp; fit</summary>
                <dl>
                  <div>
                    <dt>Measurements</dt>
                    <dd>{product.attributes.dimensions}</dd>
                  </div>
                  <div>
                    <dt>Model</dt>
                    <dd>{product.type}</dd>
                  </div>
                </dl>
              </details>
              <details>
                <summary>Material &amp; care</summary>
                <p>{product.attributes.material}</p>
                <p>
                  Wipe carefully with a soft, dry cloth. Keep away from prolonged
                  moisture and store in the supplied dust bag.
                </p>
              </details>
              <details>
                <summary>Responsibility</summary>
                <p>
                  Designed for long-term use, repair and considered everyday
                  care. Packaging is paper based and recyclable where facilities
                  exist.
                </p>
              </details>
            </div>
          </div>
        </aside>
      </div>

      {recommendations.length > 0 && (
        <section className="product-recommendations">
          <header>
            <p>Selected for you</p>
            <h2>You may also like</h2>
          </header>
          <div>
            {recommendations.map((item) => (
              <ProductCard key={item.id} product={item} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
